import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Layout from "../components/Layout";
import SEO from "../components/SEO";
import SectionHeading from "../components/SectionHeading";
import { api } from "../lib/api";
import { IMAGES } from "../lib/images";
import { fileUrl } from "../lib/storage";

const projectImage = (p) => {
  if (p.hero_image_url) return fileUrl(p.hero_image_url);
  if (p.hero_image && IMAGES[p.hero_image]) return IMAGES[p.hero_image];
  return IMAGES.elliott_twin_towers;
};

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  return (
    <Layout>
      <SEO title="Developments" description="A portfolio of landmark mixed-use, branded residence and waterfront developments by Elliott International Development — Tampa, Florida." path="/projects" />
      <section className="pt-40 pb-16 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto">
          <div className="overline mb-6">— Developments</div>
          <h1 className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.98] font-light tracking-tight max-w-5xl">
            A portfolio of <em className="text-primary italic">landmark</em> developments.
          </h1>
          <p className="mt-8 text-muted-foreground text-lg max-w-2xl leading-relaxed">
            From transformational twin towers to waterfront ultra-luxury and
            master-planned urban quarters — every EID development is conceived
            as a generational asset.
          </p>
        </div>
      </section>

      <section className="py-16 lg:py-24 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto grid grid-cols-1 lg:grid-cols-12 gap-6 auto-rows-[minmax(380px,_auto)]">
          {projects.map((p, i) => {
            const spans = i === 0
              ? "lg:col-span-8 lg:row-span-2"
              : i === 1
              ? "lg:col-span-4"
              : "lg:col-span-4";
            return (
              <motion.div
                key={p.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.9, delay: i * 0.1, ease: [0.16, 1, 0.3, 1] }}
                className={spans}
              >
                <Link
                  to={p.id === "elliott-towers" ? "/elliott-towers" : `/projects/${p.id}`}
                  data-testid={`project-card-${p.id}`}
                  className="group relative block h-full min-h-[420px] overflow-hidden"
                >
                  <img
                    src={projectImage(p)}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover img-grayscale-hover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-background via-background/30 to-transparent" />
                  <div className="absolute inset-0 p-8 lg:p-10 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div className="glass px-3 py-1 font-mono text-[10px] uppercase tracking-[0.25em] text-primary">
                        {p.status}
                      </div>
                      <ArrowUpRight className="w-6 h-6 text-primary transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                    </div>
                    <div>
                      <div className="overline mb-3 text-primary/80">{p.type}</div>
                      <h3 className="font-serif text-3xl md:text-5xl tracking-tight leading-[1.05]">{p.name}</h3>
                      <p className="mt-3 text-muted-foreground max-w-md">{p.tagline}</p>
                      <div className="mt-6 flex flex-wrap gap-6 font-mono text-xs">
                        <div><span className="text-foreground/50">Location · </span><span className="text-primary">{p.location}</span></div>
                        <div><span className="text-foreground/50">Year · </span><span className="text-primary">{p.year}</span></div>
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="py-32 px-6 lg:px-12">
        <div className="max-w-[1600px] mx-auto text-center">
          <SectionHeading
            align="center"
            overline="— Pipeline"
            title="The next chapter is being authored."
            subtitle="Additional opportunities across the Florida Gulf Coast are under evaluation. Capital partners are invited to inquire."
          />
          <Link
            to="/investor-relations"
            data-testid="projects-cta-investor"
            className="mt-12 inline-flex items-center gap-3 bg-primary text-primary-foreground px-8 py-5 text-[13px] uppercase tracking-[0.22em] hover:bg-primary/90 transition-colors"
          >
            Investor Relations <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </Layout>
  );
}
